import React, { useState, useEffect, useContext } from 'react';
import './FinancialTable.css';
import Sidebar from '../../components/Sidebar/Sidebar';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Accordion from '../../components/Accordion/Accordion';
import { ValuationContext } from '../../context/ValuationContext'
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { FiDownload, FiInfo } from 'react-icons/fi';
import { resultKeys, expenseKeys } from './FinancialTableKeys';
import { FiCalendar, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { isDcfOnlyCompanyStage } from '../sectorOptions';

const years = ['2025', '2026', '2027', '2028', '2029'];
const allPossibleYears = ['2025', '2026', '2027', '2028', '2029', '2030'];
const initialYearData = { '2025': 0, '2026': 0, '2027': 0, '2028': 0, '2029': 0, '2030': 0 };

const sampleInitialData = {
    hasRevenue2025: 'Hayır',
    revenues: [{ id: 1, name: '', annualRevenue: { '2025': 0, '2026': 0, '2027': 0, '2028': 0, '2029': 0, '2030': 0 } }],
    salesReturns: { ...initialYearData },
    grants: { ...initialYearData },
    productionExpenses: { ...initialYearData },
    shippingAndCustoms: { ...initialYearData },
    spoilage: { ...initialYearData },
    supportAndMaintenance: { ...initialYearData },
    productionMachinery: { ...initialYearData },
    digitalMarketingExpenses: { ...initialYearData },
    printedAds: { ...initialYearData },
    outdoorAds: { ...initialYearData },
    radioTvAds: { ...initialYearData },
    salesStaffExpenses: { ...initialYearData },
    commissions: { ...initialYearData },
    domesticTravelExpenses: { ...initialYearData },
    internationalTravelExpenses: { ...initialYearData },
    promotionEventExpenses: { ...initialYearData },
    shippingDeliveryExpenses: { ...initialYearData },
    salesStaffAssets: { ...initialYearData },
    rdPersonnelExpenses: { ...initialYearData },
    consultancyExpenses: { ...initialYearData },
    testPrototypeExpenses: { ...initialYearData },
    materialExpenses: { ...initialYearData },
    rdSoftwareHardwareExpenses: { ...initialYearData },
    patentLicenseExpenses: { ...initialYearData },
    universityCollaborationExpenses: { ...initialYearData },
    rdFacilityExpenses: { ...initialYearData },
    rdIncentiveExpenses: { ...initialYearData },
    outsourcingExpenses: { ...initialYearData },
    rdTravelExpenses: { ...initialYearData },
    rdEquipmentExpenses: { ...initialYearData },
    managementSalaries: { ...initialYearData },
    hrAccountingExpenses: { ...initialYearData },
    legalConsultancyExpenses: { ...initialYearData },
    officeEquipmentExpenses: { ...initialYearData },
    generalOfficeExpenses: { ...initialYearData },
    rentExpenses: { ...initialYearData },
    communicationExpenses: { ...initialYearData },
    trainingExpenses: { ...initialYearData },
    managementTravelExpenses: { ...initialYearData },
    softwareLicenses: { ...initialYearData },
    representationExpenses: { ...initialYearData },
    insuranceExpenses: { ...initialYearData },
    consultingExpenses: { ...initialYearData },
    membershipExpenses: { ...initialYearData },
    creditInterests: { ...initialYearData },
    bankCommissions: { ...initialYearData },
    leasingExpenses: { ...initialYearData },
    maturityDifferences: { ...initialYearData },
    depreciationExpense: { ...initialYearData },
    taxAmount: { ...initialYearData },
    depreciationExpenses: { ...initialYearData },
};

const FinancialTable = () => {
    const { t, i18n } = useTranslation();
    const [hasRevenue2025, setHasRevenue2025] = useState('Hayır');

    const { valuationData, updateFinancials, currency, valuationCurrency } = useContext(ValuationContext);
    const isDcfOnly = isDcfOnlyCompanyStage(valuationData.entrepreneurInfo?.stage);
    const [inputs, setInputs] = useState(sampleInitialData);
    const [isSaving, setIsSaving] = useState(false);
    const [plansForGrant, setPlansForGrant] = useState('Evet');
    const [hasDepreciation, setHasDepreciation] = useState('Evet');
    const navigate = useNavigate();
    const [exchangeRate, setExchangeRate] = useState(1);
    const [isLoadingRate, setIsLoadingRate] = useState(false);
    const [results, setResults] = useState({
        totalRevenue: { ...initialYearData },
        netSalesRevenue: { ...initialYearData },
        totalIncome: { ...initialYearData },
        costOfSales: { ...initialYearData },
        grossProfit: { ...initialYearData },
        gpmPercent: { ...initialYearData },
        ebit: { ...initialYearData },
        netIncome: { ...initialYearData },
        ebitda: { ...initialYearData },
        discountedEbitda: { ...initialYearData },
        terminalValue: 0,
        valuationMetric: 0,
        yoyGrowthWarnings: { ...initialYearData },
        totalOperationalExpenses: { ...initialYearData },
        totalMarketingSalesExpenses: { ...initialYearData },
        totalRDExpenses: { ...initialYearData },
        totalGeneralAdminExpenses: { ...initialYearData },
        totalFinancingExpenses: { ...initialYearData },
        totalDepreciation: { ...initialYearData },
        percentageEbit: { ...initialYearData },
        percentageEbitda: { ...initialYearData },
    });


    const activeYears = React.useMemo(() => {
        if (hasRevenue2025 === 'Hayır') {
            // 2025 yoksa: 2026'dan başla, 2030'a kadar git (5 Yıl)
            return ['2026', '2027', '2028', '2029', '2030'];
        } else {
            // 2025 varsa: 2025'ten başla, 2029'da bitir (5 Yıl)
            return ['2025', '2026', '2027', '2028', '2029'];
        }
    }, [hasRevenue2025]);

    // Sayfa yüklendiğinde scroll'u en üste al
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        loadDraftData();
    }, []);

    useEffect(() => {
        const newResults = {
            totalRevenue: { ...initialYearData },
            netSalesRevenue: { ...initialYearData },
            totalIncome: { ...initialYearData },
            costOfSales: { ...initialYearData },
            grossProfit: { ...initialYearData },
            gpmPercent: { ...initialYearData },
            ebit: { ...initialYearData },
            netIncome: { ...initialYearData },
            ebitda: { ...initialYearData },
            discountedEbitda: { ...initialYearData },
            terminalValue: 0,
            valuationMetric: 0,
            yoyGrowthWarnings: { ...initialYearData },
            totalMarketingSalesExpenses: { ...initialYearData },
            totalRDExpenses: { ...initialYearData },
            totalGeneralAdminExpenses: { ...initialYearData },
            totalFinancingExpenses: { ...initialYearData },
            totalDepreciation: { ...initialYearData },
            percentageEbit: { ...initialYearData },
            percentageEbitda: { ...initialYearData },
        };
        newResults.hasRevenue2025 = hasRevenue2025; 
        activeYears.forEach((year, index) => {
            // Calculate total revenue from all products
            const totalRevenueForYear = inputs.revenues.reduce((acc, product) => {
                return acc + (product.annualRevenue[year] || 0);
            }, 0);

            const netSalesRevenue = totalRevenueForYear - (inputs.salesReturns[year] || 0);
            const totalIncome = netSalesRevenue + (inputs.grants[year] || 0);

            const costOfSales = (inputs.productionExpenses[year] || 0) +
                (inputs.shippingAndCustoms[year] || 0) +
                (inputs.spoilage[year] || 0) +
                (inputs.supportAndMaintenance[year] || 0) +
                (inputs.productionMachinery[year] || 0);

            const marketingSalesExpenses = (inputs.digitalMarketingExpenses[year] || 0) +
                (inputs.printedAds[year] || 0) +
                (inputs.outdoorAds[year] || 0) +
                (inputs.radioTvAds[year] || 0) +
                (inputs.salesStaffExpenses[year] || 0) +
                (inputs.commissions[year] || 0) +
                (inputs.domesticTravelExpenses[year] || 0) +
                (inputs.internationalTravelExpenses[year] || 0) +
                (inputs.promotionEventExpenses[year] || 0) +
                (inputs.salesStaffAssets[year] || 0) +
                (inputs.shippingDeliveryExpenses[year] || 0);

            const rdExpenses = (inputs.rdPersonnelExpenses[year] || 0) +
                (inputs.consultancyExpenses[year] || 0) +
                (inputs.testPrototypeExpenses[year] || 0) +
                (inputs.materialExpenses[year] || 0) +
                (inputs.rdSoftwareHardwareExpenses[year] || 0) +
                (inputs.patentLicenseExpenses[year] || 0) +
                (inputs.universityCollaborationExpenses[year] || 0) +
                (inputs.rdFacilityExpenses[year] || 0) +
                (inputs.rdIncentiveExpenses[year] || 0) +
                (inputs.outsourcingExpenses[year] || 0) +
                (inputs.rdEquipmentExpenses[year] || 0) +
                (inputs.rdTravelExpenses[year] || 0);

            const generalAdminExpenses = (inputs.managementSalaries[year] || 0) +
                (inputs.hrAccountingExpenses[year] || 0) +
                (inputs.legalConsultancyExpenses[year] || 0) +
                (inputs.officeEquipmentExpenses[year] || 0) +
                (inputs.generalOfficeExpenses[year] || 0) +
                (inputs.rentExpenses[year] || 0) +
                (inputs.communicationExpenses[year] || 0) +
                (inputs.trainingExpenses[year] || 0) +
                (inputs.managementTravelExpenses[year] || 0) +
                (inputs.softwareLicenses[year] || 0) +
                (inputs.representationExpenses[year] || 0) +
                (inputs.insuranceExpenses[year] || 0) +
                (inputs.consultingExpenses[year] || 0) +
                (inputs.membershipExpenses[year] || 0);

            const financingExpenses = (inputs.creditInterests[year] || 0) +
                (inputs.bankCommissions[year] || 0) +
                (inputs.leasingExpenses[year] || 0) +
                (inputs.maturityDifferences[year] || 0);

            const totalDepreciation = inputs.depreciationExpenses[year] || 0;

            // Sonra ana hesaplamaları yapın
            const grossProfit = totalIncome - (marketingSalesExpenses + rdExpenses + generalAdminExpenses + costOfSales + financingExpenses);
            const ebit = grossProfit - (inputs.taxAmount[year] || 0);
            const ebitda = ebit + totalDepreciation + (inputs.taxAmount[year] || 0) + financingExpenses;


            const percentageEbit = totalIncome > 0 ? (ebit / totalIncome) : 0;
            const percentageEbitda = totalIncome > 0 ? (ebitda / totalIncome) : 0;



            // Sonuçları kaydet
            newResults.totalRevenue[year] = totalRevenueForYear;
            newResults.netSalesRevenue[year] = netSalesRevenue;
            newResults.totalIncome[year] = totalIncome;
            newResults.costOfSales[year] = costOfSales;
            newResults.grossProfit[year] = grossProfit;
            newResults.gpmPercent[year] = totalIncome > 0 ? (grossProfit / totalIncome) * 100 : 0;
            newResults.ebit[year] = ebit;
            newResults.ebitda[year] = ebitda;
            newResults.totalMarketingSalesExpenses[year] = marketingSalesExpenses;
            newResults.totalRDExpenses[year] = rdExpenses;
            newResults.totalGeneralAdminExpenses[year] = generalAdminExpenses;
            newResults.totalFinancingExpenses[year] = financingExpenses;
            newResults.totalDepreciation[year] = totalDepreciation;
            newResults.percentageEbit[year] = percentageEbit;
            newResults.percentageEbitda[year] = percentageEbitda;

            const netIncome = ebit - financingExpenses - (inputs.taxAmount[year] || 0);
            newResults.netIncome[year] = netIncome;

            if (index > 0) {
                const prevYear = activeYears[index - 1];
                const prevIncome = newResults.totalIncome[prevYear] || 0;
                const currentIncome = totalIncome;
                let growthRate = 0;

                if (prevIncome > 0) {
                    growthRate = ((currentIncome - prevIncome) / prevIncome) * 100;
                }

                let warningMessage = "";
                if (growthRate > 150) {
                    warningMessage = t('financialTable.warnings.superHighGrowth');
                } else if (growthRate > 100) {
                    warningMessage = t('financialTable.warnings.highGrowth');
                } else if (growthRate > 50) {
                    warningMessage = t('financialTable.warnings.mediumGrowth');
                } else if (growthRate > 25) {
                    warningMessage = t('financialTable.warnings.sustainableGrowth'); 
                } else if (growthRate >= 0) {
                    warningMessage = t('financialTable.warnings.lowGrowth');
                } else {
                    warningMessage = t('financialTable.warnings.negativeGrowth');
                }

                newResults.yoyGrowthWarnings[year] = warningMessage;
            } else {
                newResults.yoyGrowthWarnings[year] = "-";
            }

            const discountRate = 0.40;
            const period = index + 1;           
            const discountedValue = ebitda / Math.pow((1 + discountRate), period);
            newResults.discountedEbitda[year] = discountedValue* exchangeRate * 5;
        });

        const lastYear = activeYears[activeYears.length - 1]; // '2029' veya '2030'
        const lastYearEbitda = newResults.ebitda[lastYear];        const perpetualGrowthRate = 0.025;
        const terminalDiscountRate = 0.30;
        if (terminalDiscountRate > perpetualGrowthRate) {
            newResults.terminalValue = (lastYearEbitda * (1 + perpetualGrowthRate)) / (terminalDiscountRate - perpetualGrowthRate);
        }

        const val1 = newResults.discountedEbitda[activeYears[2]]; // 3. sütun
        const val2 = newResults.discountedEbitda[activeYears[3]]; // 4. sütun
        const val3 = newResults.discountedEbitda[activeYears[4]]; // 5. sütun
        
        newResults.valuationMetric = (val1 + val2 + val3) / 3;
        setResults(newResults);



    }, [inputs, exchangeRate, t, activeYears, hasRevenue2025]);
    const loadDraftData = async () => {
        try {
            const token = localStorage.getItem('userToken');
            const response = await fetch('/api/drafts/step/financial-table', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.exists && data.data) {
                    if (data.data.inputs) {
                        // depreciationExpenses yoksa ekle
                        if (!data.data.inputs.depreciationExpenses) {
                            data.data.inputs.depreciationExpenses = { ...initialYearData };
                        }
                        setInputs(data.data.inputs);
                        if (data.data.inputs.hasRevenue2025) {
                            setHasRevenue2025(data.data.inputs.hasRevenue2025);
                        } else {
                            setHasRevenue2025('Evet'); // Veri yoksa varsayılanı ata
                        }
                        setIsSaved(true);
                    }
                }
            }
        } catch (error) {
            console.error('Draft verisi yüklenirken hata:', error);
        }
    };

    const handleRevenue2025Change = (e) => {
        setIsSaved(false);
        const val = e.target.value;
        setHasRevenue2025(val);
        setInputs(prev => ({
            ...prev,
            hasRevenue2025: val
        }));
    };

    const [isSaved, setIsSaved] = useState(false); // Bunu component'in üst kısmına ekle
    const [hoveredYear, setHoveredYear] = useState(null);

    const handleBack = () => {
        // Eğer kaydetme işlemi yapılmışsa direkt git
        if (isSaved) {
            navigate('/entrepreneur-info');
            return;
        }

        // Herhangi bir input değişmiş mi kontrol et
        const hasChanges = Object.keys(inputs).some(key => {
            if (key === 'revenues') {
                return inputs[key].some(product =>
                    product.name !== `Ürün/Hizmet ${inputs.revenues.indexOf(product) + 1}` ||
                    Object.values(product.annualRevenue).some(value => value > 0)
                );
            }
            return Object.values(inputs[key]).some(value => value > 0);
        });

        if (hasChanges) {
            const confirmLeave = window.confirm(
                'Kaydedilmemiş değişiklikleriniz var. Kaydetmeden devam etmek istediğinizden emin misiniz?'
            );
            if (!confirmLeave) {
                return;
            }
        }

        navigate('/entrepreneur-info');
    };

    // Kaydet fonksiyonu
    // FinancialTable.js içindeki saveDraft fonksiyonunu bununla değiştirin:
    const saveDraft = async () => {
        setIsSaving(true);
        try {
            // GÜVENLİK ÖNLEMİ: hasRevenue2025 tercihini inputs içine zorla ekliyoruz
            const finalInputs = { 
                ...inputs, 
                hasRevenue2025: hasRevenue2025 
            };

            const token = localStorage.getItem('userToken');
            const response = await fetch('/api/drafts/save', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    step: 'financial-table',
                    data: {
                        inputs: finalInputs, // inputs yerine finalInputs gönderiyoruz
                        results: results
                    }
                })
            });

            if (response.ok) {
                // Kayıt başarılıysa state'i de güncelle ki senkron kalsın
                setInputs(finalInputs);
                setIsSaved(true);
                alert(t('financialTable.saveSuccess'));
            } else {
                throw new Error('Kaydetme başarısız');
            }
        } catch (error) {
            console.error('Kaydetme hatası:', error);
            alert(t('financialTable.saveError'));
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        fetchExchangeRate();
    }, [currency, valuationCurrency]);

    const handleProductNameChange = (index, newName) => {
        setIsSaved(false);
        const updatedRevenues = [...inputs.revenues];
        updatedRevenues[index].name = newName;
        setInputs(prevInputs => ({ ...prevInputs, revenues: updatedRevenues }));
    };

    const handleProductRevenueChange = (index, year, value) => {
        setIsSaved(false);
        const numericValue = parseFloat(value) || 0;
        const updatedRevenues = JSON.parse(JSON.stringify(inputs.revenues));
        updatedRevenues[index].annualRevenue[year] = numericValue;
        setInputs(prevInputs => ({ ...prevInputs, revenues: updatedRevenues }));
    };

    const handleOnFocusRevenue = (index, year, value) => {
        if (value == 0) {
            const updatedRevenues = JSON.parse(JSON.stringify(inputs.revenues));
            updatedRevenues[index].annualRevenue[year] = null;
            setInputs(prevInputs => ({ ...prevInputs, revenues: updatedRevenues }));
        }
    }

    const handleOnBlurRevenue = (index, year, value) => {
        if (value == '') {
            const updatedRevenues = JSON.parse(JSON.stringify(inputs.revenues));
            updatedRevenues[index].annualRevenue[year] = 0;
            setInputs(prevInputs => ({ ...prevInputs, revenues: updatedRevenues }));
        }
    }

    const handleOnFocus = (field, year, value) => {
        if (value == 0) {
            setInputs(prevInputs => ({ ...prevInputs, [field]: { ...prevInputs[field], [year]: null } }));
        }
    }

    const handleOnBlur = (field, year, value) => {
        if (value == '') {
            setInputs(prevInputs => ({ ...prevInputs, [field]: { ...prevInputs[field], [year]: 0 } }));
        }
    }

    const handleInputChange = (field, year, value) => {
        setIsSaved(false);
        const numericValue = parseFloat(value) || 0;
        setInputs(prevInputs => ({ ...prevInputs, [field]: { ...prevInputs[field], [year]: numericValue } }));
    };

    const handleAddRevenueRow = () => {
        setIsSaved(false);
        const newProduct = {
            id: Date.now(),
            name: ``,
            annualRevenue: { ...initialYearData }
        };
        setInputs(prevInputs => ({ ...prevInputs, revenues: [...prevInputs.revenues, newProduct] }));
    };

    const handleGrantPlanChange = (event) => {
        setIsSaved(false);
        const { value } = event.target;
        setPlansForGrant(value);
        if (value === 'Hayır') {
            setInputs(prevInputs => ({ ...prevInputs, grants: { ...initialYearData } }));
        }
    };

    const handleDepreciationChange = (event) => {
        setIsSaved(false);
        const { value } = event.target;
        setHasDepreciation(value);
        if (value === 'Hayır') {
            setInputs(prevInputs => ({ ...prevInputs, depreciationExpense: { ...initialYearData } }));
        }
    };

    const handleNext = async () => {
        // 1. ADIM: "Hayır" seçimini GARANTİ ALTINA ALALIM
        // inputs state'inin güncellenmesini beklemeden, o anki radyo butonu değerini (hasRevenue2025)
        // zorla verinin içine ekliyoruz.
        const finalInputs = { 
            ...inputs, 
            hasRevenue2025: hasRevenue2025 // Burada state'teki güncel değeri kullanıyoruz
        };

        // 2. ADIM: Arka Planda Veritabanına Kaydet
        try {
            const token = localStorage.getItem('userToken');
            await fetch('/api/drafts/save', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    step: 'financial-table',
                    data: {
                        inputs: finalInputs, // inputs yerine oluşturduğumuz finalInputs'u gönderiyoruz
                        results: results
                    }
                })
            });
        } catch (error) {
            console.error('Otomatik kaydetme hatası:', error);
            // Hata olsa bile kullanıcıyı durdurmuyoruz, devam etsin.
        }

        // 3. ADIM: Uygulama Hafızasını (Context) Güncelle
        // Rapor sayfası veriyi buradan okuyabilir, o yüzden burası da güncel olmalı.
        updateFinancials(finalInputs, results);
        
        // 4. ADIM: Sonraki Sayfaya Git
        navigate('/assessment');
    };

    const formatNumber = (num) => {
        if (num == null) return '';
        return new Intl.NumberFormat('tr-TR').format(Math.round(num));
    };

    const formatPercent = (num) => {
        if (isNaN(num) || !isFinite(num)) {
            return '% 0.00';
        }
        return `% ${num.toFixed(2)}`;
    }

    const fetchExchangeRate = async () => {
        if (currency === valuationCurrency) {
            setExchangeRate(1);
            return;
        }

        setIsLoadingRate(true);
        try {
            const response = await fetch(`https://api.exchangerate-api.com/v4/latest/EUR`);
            const data = await response.json();

            const getEurRate = (currencySymbol) => {
                if (currencySymbol === '€') return 1;
                if (currencySymbol === '$') return data.rates.USD || 1;
                if (currencySymbol === '₺') return data.rates.TRY || 1;
                return 1;
            };

            const fromRate = getEurRate(currency);
            const toRate = getEurRate(valuationCurrency);
            const exchangeRate = toRate / fromRate;
            setExchangeRate(exchangeRate);

        } catch (error) {
            console.error('Döviz kuru alınamadı:', error);
            setExchangeRate(1);
        } finally {
            setIsLoadingRate(false);
        }
    };

  const handleExportExcel = () => {
        const yearsStr = activeYears.map(y => String(y));
        // Başlıkları çevir
        const headers = [t('financialTable.excel.section'), t('financialTable.excel.item'), ...yearsStr];
    
        const makeRow = (bolum, kalem, perYearObj = null) => {
            // Bölüm ve Kalem başlıkları artık t() ile dışarıdan geliyor
            const row = { 
                [t('financialTable.excel.section')]: bolum, 
                [t('financialTable.excel.item')]: kalem 
            };
            yearsStr.forEach(y => {
                const val = perYearObj ? (perYearObj[y] ?? perYearObj[+y] ?? 0) : 0;
                row[y] = kalem.includes("%") ? formatPercent(val) : currency + formatNumber(val)
            });
            return row;
        };
    
        // "Ürün/Hizmet" yazısını çevir
        const revenueRows = (inputs.revenues || []).map(product =>
            makeRow(t('financialTable.excel.revenues'), product?.name || t('financialTable.productServiceName'), product?.annualRevenue || {})
        );
    
        // Satır isimlerini mevcut çeviri anahtarlarından (keys) çekiyoruz
        const totalRevenueRow = makeRow(t('financialTable.excel.revenues'), t('financialTable.totalIncome'), results.totalIncome || {});
    
        const expenseRows = [
            { key: "costOfSales", value: t('financialTable.totalCostOfSales') },
            { key: "totalMarketingSalesExpenses", value: t('financialTable.totalMarketingSalesExpenses') }, // Bu key json'da yoksa ekleyin: "Toplam Pazarlama ve Satış Giderleri"
            { key: "totalRDExpenses", value: t('financialTable.totalRDExpensesDetail') },
            { key: "totalGeneralAdminExpenses", value: t('financialTable.totalGeneralAdminExpensesDetail') },
            { key: "totalFinancingExpenses", value: t('financialTable.totalFinancingExpensesDetail') }
        ].map(item => {
            const val = results[item.key] || {};
            return makeRow(t('financialTable.excel.expenses'), item.value, val);
        });
    
        const brutKarRow = makeRow(t('financialTable.excel.results'), t('financialTable.grossProfitLabel'), results.grossProfit || {});
    
        const amortismanRow = makeRow(t('financialTable.excel.expenses'), t('financialTable.depreciationExpenses'), inputs.depreciationExpenses || {});
        const vergiRow = makeRow(t('financialTable.excel.expenses'), t('financialTable.taxAmount'), inputs.taxAmount || {});
    
        const resultRows = [
            { key: "ebit", value: t('financialTable.ebit') },
            { key: "percentageEbit", value: t('financialTable.percentageEbit') },
            { key: "ebitda", value: t('financialTable.ebitda') },
            { key: "percentageEbitda", value: t('financialTable.percentageEbitda') }
        ].map(item => {
            const val = results[item.key] || {};
            return makeRow(t('financialTable.excel.results'), item.value, val);
        });
    
        const combined = [
            ...revenueRows, 
            totalRevenueRow, 
            ...expenseRows,
            brutKarRow,
            amortismanRow,
            vergiRow,
            ...resultRows
        ];
    
        // json_to_sheet header opsiyonunu güncelle
        const ws = XLSX.utils.json_to_sheet(combined, { header: headers });
        const range = XLSX.utils.decode_range(ws["!ref"]);
    
        // ... (Stil döngüsü aynı kalabilir, sadece mantık değişmedi) ... 
        for (let R = range.s.r; R <= range.e.r; ++R) {
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
                if (!ws[cellAddress]) continue;
    
                const borderStyle = {
                    top: { style: "thin", color: { rgb: "000000" } },
                    bottom: { style: "thin", color: { rgb: "000000" } },
                    left: { style: "thin", color: { rgb: "000000" } },
                    right: { style: "thin", color: { rgb: "000000" } }
                };
    
                // Yüzde kontrolü için sütun B'ye (index 1) bakılıyor
                const bColumnAddress = XLSX.utils.encode_cell({ r: R, c: 1 });
                const kalemValue = ws[bColumnAddress] && ws[bColumnAddress].v ? ws[bColumnAddress].v.toString() : "";
                const isPercentRow = kalemValue.includes("%");
    
                // Stil atamaları aynı kalabilir
                if (R === 0) {
                     ws[cellAddress].s = {
                        font: { bold: true, sz: 11 },
                        fill: { fgColor: { rgb: "E0E0E0" } },
                        alignment: { horizontal: "center", vertical: "center" },
                        border: borderStyle
                    };
                } else if (C === 0) {
                    ws[cellAddress].s = {
                        font: { bold: true, sz: 11 },
                        alignment: { horizontal: "left", vertical: "center" },
                        border: borderStyle,
                        fill: isPercentRow ? { fgColor: { rgb: "F5F5F5" } } : undefined
                    };
                } else if (C === 1) {
                     ws[cellAddress].s = {
                        alignment: { horizontal: "left", vertical: "center" },
                        border: borderStyle,
                        fill: isPercentRow ? { fgColor: { rgb: "F5F5F5" } } : undefined
                    };
                } else {
                     ws[cellAddress].s = {
                        alignment: { horizontal: "right", vertical: "center" },
                        border: borderStyle,
                        fill: isPercentRow ? { fgColor: { rgb: "F5F5F5" } } : undefined
                    };
                }
            }
        }
    
        ws["!cols"] = [
            { wch: 15 }, // Bölüm sütunu biraz genişletildi
            { wch: 45 },
            { wch: 12 },
            { wch: 12 },
            { wch: 12 },
            { wch: 12 },
            { wch: 12 },
            { wch: 12 }
        ];
    
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Finansal_Tablo"); // İstersen burayı da t('financialTable.title') yapabilirsin
    
        const wbout = XLSX.write(wb, {
            bookType: "xlsx",
            type: "array",
            cellStyles: true
        });
    
        saveAs(new Blob([wbout], { type: "application/octet-stream" }), `${t('financialTable.excel.fileName')}.xlsx`);
    };

    return (
        <div className="financial-table-page">
            <div className="financial-table-container">
                <Sidebar />
                {!isDcfOnly && <ProgressBar />}
                <h2>{t('financialTable.title')}</h2>
                <div className="revenue-question-card">
                    <div className="revenue-question-text">
                        <FiCalendar size={24} color="#3b82f6" />
                        {t('financialTable.hasRevenue2025Question')}
                    </div>
                    
                    <div className="revenue-toggle-group">
                        <label 
                            className={`revenue-toggle-option ${hasRevenue2025 === 'Evet' ? 'active' : ''}`}
                        >
                            <input 
                                type="radio" 
                                value="Evet" 
                                checked={hasRevenue2025 === 'Evet'} 
                                onChange={handleRevenue2025Change} 
                            />
                            <FiCheckCircle size={16} />
                            {t('financialTable.yes')}
                        </label>
                        
                        <label 
                            className={`revenue-toggle-option ${hasRevenue2025 === 'Hayır' ? 'active' : ''}`}
                        >
                            <input 
                                type="radio" 
                                value="Hayır" 
                                checked={hasRevenue2025 === 'Hayır'} 
                                onChange={handleRevenue2025Change}
                            />
                            <FiXCircle size={16} />
                            {t('financialTable.no')}
                        </label>
                    </div>
                </div>
                {/* Modern Soru Alanı Sonu */}

                <p>{t('financialTable.subtitle')}</p>
                <div className="table-wrapper-finance">
                    <table>
                        <thead>
                            <tr>
                                <th>{t('financialTable.revenues')}</th>
                                {activeYears.map(year => <th key={year}>{year}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {inputs.revenues.map((product, index) => (
                                <React.Fragment key={product.id}>
                                    <tr className="input-row product-header-row">
                                        <td>
                                            <input
                                                type="text"
                                                value={product.name}
                                                onChange={(e) => handleProductNameChange(index, e.target.value.replace(/\./g, ""))}
                                                className="product-name-input"
                                                placeholder={t('financialTable.productServiceName')}
                                            />
                                        </td>
                                        {activeYears.map(year => <td key={year} className="placeholder-cell"></td>)}
                                    </tr>
                                    <tr className="input-row">
                                        <td className="sub-item">{t('financialTable.annualRevenue')} ({currency})</td>
                                        {activeYears.map(year => (
                                            <td key={year}>
                                                <input
                                                    type="text"
                                                    value={formatNumber(product.annualRevenue[year])}
                                                    onChange={(e) => handleProductRevenueChange(index, year, e.target.value.replace(/\./g, ""))}
                                                    onFocus={(e) => handleOnFocusRevenue(index, year, e.target.value)}
                                                    onBlur={(e) => handleOnBlurRevenue(index, year, e.target.value)}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                </React.Fragment>
                            ))}
                            <tr>
                                <td colSpan={activeYears.length + 1} className="add-row-cell">
                                    <button type="button" onClick={handleAddRevenueRow} className="add-row-button">
                                        {t('financialTable.addRevenue')}
                                    </button>
                                </td>
                            </tr>


                            <tr className="input-row">
                                <td>{t('financialTable.salesReturnsAndDiscounts')}</td>
                                {activeYears.map(year => (
                                    <td key={year}>
                                        <input
                                            type="text"
                                            value={formatNumber(inputs.salesReturns[year])}
                                            onChange={(e) => handleInputChange('salesReturns', year, e.target.value.replace(/\./g, ""))}
                                            onFocus={(e) => handleOnFocus('salesReturns', year, e.target.value)}
                                            onBlur={(e) => handleOnBlur('salesReturns', year, e.target.value)}
                                        />
                                    </td>
                                ))}
                            </tr>

                            <tr className="total-row">
                                <td>{t('financialTable.netSalesRevenue')}</td>
                                {activeYears.map(year => <td key={year}>{currency}{formatNumber(results.netSalesRevenue[year])}</td>)}
                            </tr>

                            {plansForGrant === 'Evet' && (
                                <tr className="input-row">
                                    <td>{t('financialTable.otherIncome')}</td>
                                    {activeYears.map(year => (
                                        <td key={year}>
                                            <input
                                                type="text"
                                                value={formatNumber(inputs.grants[year])}
                                                onChange={(e) => handleInputChange('grants', year, e.target.value.replace(/\./g, ""))}
                                                onFocus={(e) => handleOnFocus('grants', year, e.target.value)}
                                                onBlur={(e) => handleOnBlur('grants', year, e.target.value)}
                                            />
                                        </td>
                                    ))}
                                </tr>
                            )}

                            <tr className="total-row">
                                <td>{t('financialTable.totalIncome')}</td>
                                {activeYears.map(year => (
                                    <td
                                        key={year}
                                        className="growth-analysis-cell"
                                        onMouseEnter={() => setHoveredYear(year)}
                                        onMouseLeave={() => setHoveredYear(null)}
                                        style={{ position: 'relative', cursor: 'pointer' }}
                                    >
                                        {currency}{formatNumber(results.totalIncome[year])}
                                        {results.yoyGrowthWarnings[year] !== '-' && results.totalIncome[year] > 0 && (
                                            <FiInfo
                                                style={{
                                                    marginLeft: '5px',
                                                    fontSize: '14px',
                                                    verticalAlign: 'middle',
                                                    color: '#f63b3b'
                                                }}
                                            />
                                        )}
                                        {hoveredYear === year && results.yoyGrowthWarnings[year] !== '-' && results.totalIncome[year] > 0 && (
                                            <div className="growth-tooltip">
                                                {results.yoyGrowthWarnings[year]}
                                            </div>
                                        )}
                                    </td>
                                ))}
                            </tr>

                            <tr className="header-row">
                                <td colSpan={activeYears.length + 1}>{t('financialTable.expenses')}</td>
                            </tr>

                            <tr>
                                <td colSpan={activeYears.length + 1} className="accordion-cell">
                                    <Accordion title={t('financialTable.cogsTitle')}>
                                        <table className="inner-table">
                                            <tbody>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.productionExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.productionExpenses[year])}
                                                                onChange={(e) => handleInputChange('productionExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('productionExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('productionExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>

                                                <tr className="input-row">
                                                    <td>{t('financialTable.shippingAndCustoms')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.shippingAndCustoms[year])}
                                                                onChange={(e) => handleInputChange('shippingAndCustoms', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('shippingAndCustoms', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('shippingAndCustoms', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.spoilage')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.spoilage[year])}
                                                                onChange={(e) => handleInputChange('spoilage', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('spoilage', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('spoilage', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.supportAndMaintenance')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.supportAndMaintenance[year])}
                                                                onChange={(e) => handleInputChange('supportAndMaintenance', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('supportAndMaintenance', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('supportAndMaintenance', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.productionMachinery')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.productionMachinery[year])}
                                                                onChange={(e) => handleInputChange('productionMachinery', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('productionMachinery', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('productionMachinery', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="total-row inner-total-row">
                                                    <td>{t('financialTable.totalCostOfSales')}</td>
                                                    {activeYears.map(year => <td key={year}>{currency}{formatNumber(results.costOfSales[year])}</td>)}
                                                </tr>
                                            </tbody>
                                        </table>
                                    </Accordion>
                                </td>
                            </tr>

                            <tr className="header-row">
                                <td colSpan={activeYears.length + 1}>{t('financialTable.operatingExpenses')}</td>
                            </tr>

                            <tr>
                                <td colSpan={activeYears.length + 1} className="accordion-cell">
                                    <Accordion title={t('financialTable.marketingSalesTitle')}>
                                        <table className="inner-table">
                                            <tbody>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.digitalMarketingExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.digitalMarketingExpenses[year])}
                                                                onChange={(e) => handleInputChange('digitalMarketingExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('digitalMarketingExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('digitalMarketingExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.printedAds')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.printedAds[year])}
                                                                onChange={(e) => handleInputChange('printedAds', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('printedAds', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('printedAds', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.outdoorAds')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.outdoorAds[year])}
                                                                onChange={(e) => handleInputChange('outdoorAds', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('outdoorAds', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('outdoorAds', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.radioTvAds')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.radioTvAds[year])}
                                                                onChange={(e) => handleInputChange('radioTvAds', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('radioTvAds', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('radioTvAds', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.salesStaffExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.salesStaffExpenses[year])}
                                                                onChange={(e) => handleInputChange('salesStaffExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('salesStaffExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('salesStaffExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.commissions')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.commissions[year])}
                                                                onChange={(e) => handleInputChange('commissions', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('commissions', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('commissions', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.domesticTravelExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.domesticTravelExpenses[year])}
                                                                onChange={(e) => handleInputChange('domesticTravelExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('domesticTravelExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('domesticTravelExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.internationalTravelExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.internationalTravelExpenses[year])}
                                                                onChange={(e) => handleInputChange('internationalTravelExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('internationalTravelExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('internationalTravelExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.promotionEventExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.promotionEventExpenses[year])}
                                                                onChange={(e) => handleInputChange('promotionEventExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('promotionEventExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('promotionEventExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.shippingDeliveryExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.shippingDeliveryExpenses[year])}
                                                                onChange={(e) => handleInputChange('shippingDeliveryExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('shippingDeliveryExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('shippingDeliveryExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.salesStaffAssets')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.salesStaffAssets[year])}
                                                                onChange={(e) => handleInputChange('salesStaffAssets', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('salesStaffAssets', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('salesStaffAssets', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="total-row inner-total-row">
                                                    <td>{t('financialTable.totalMarketingSalesDistribution')}</td>
                                                    {activeYears.map(year => <td key={year}>{currency}{formatNumber(results.totalMarketingSalesExpenses[year])}</td>)}
                                                </tr>
                                            </tbody>
                                        </table>
                                    </Accordion>
                                </td>
                            </tr>

                            <tr>
                                <td colSpan={activeYears.length + 1} className="accordion-cell">
                                    <Accordion title={t('financialTable.rdTitle')}>
                                        <table className="inner-table">
                                            <tbody>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.rdPersonnelExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.rdPersonnelExpenses[year])}
                                                                onChange={(e) => handleInputChange('rdPersonnelExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('rdPersonnelExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('rdPersonnelExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.consultancyExpensesRD')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.consultancyExpenses[year])}
                                                                onChange={(e) => handleInputChange('consultancyExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('consultancyExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('consultancyExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.testPrototypeExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.testPrototypeExpenses[year])}
                                                                onChange={(e) => handleInputChange('testPrototypeExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('testPrototypeExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('testPrototypeExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.materialExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.materialExpenses[year])}
                                                                onChange={(e) => handleInputChange('materialExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('materialExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('materialExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.rdSoftwareHardwareExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.rdSoftwareHardwareExpenses[year])}
                                                                onChange={(e) => handleInputChange('rdSoftwareHardwareExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('rdSoftwareHardwareExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('rdSoftwareHardwareExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.patentLicenseExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.patentLicenseExpenses[year])}
                                                                onChange={(e) => handleInputChange('patentLicenseExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('patentLicenseExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('patentLicenseExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.universityCollaborationExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.universityCollaborationExpenses[year])}
                                                                onChange={(e) => handleInputChange('universityCollaborationExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('universityCollaborationExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('universityCollaborationExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.rdFacilityExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.rdFacilityExpenses[year])}
                                                                onChange={(e) => handleInputChange('rdFacilityExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('rdFacilityExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('rdFacilityExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.rdIncentiveExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.rdIncentiveExpenses[year])}
                                                                onChange={(e) => handleInputChange('rdIncentiveExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('rdIncentiveExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('rdIncentiveExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.outsourcingExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.outsourcingExpenses[year])}
                                                                onChange={(e) => handleInputChange('outsourcingExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('outsourcingExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('outsourcingExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.rdTravelExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.rdTravelExpenses[year])}
                                                                onChange={(e) => handleInputChange('rdTravelExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('rdTravelExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('rdTravelExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.rdEquipmentExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.rdEquipmentExpenses[year])}
                                                                onChange={(e) => handleInputChange('rdEquipmentExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('rdEquipmentExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('rdEquipmentExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="total-row inner-total-row">
                                                    <td>{t('financialTable.totalRDExpensesDetail')}</td>
                                                    {activeYears.map(year => <td key={year}>{currency}{formatNumber(results.totalRDExpenses[year])}</td>)}
                                                </tr>
                                            </tbody>
                                        </table>
                                    </Accordion>
                                </td>
                            </tr>

                            <tr>
                                <td colSpan={activeYears.length + 1} className="accordion-cell">
                                    <Accordion title={t('financialTable.generalAdminTitle')}>
                                        <table className="inner-table">
                                            <tbody>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.managementSalaries')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.managementSalaries[year])}
                                                                onChange={(e) => handleInputChange('managementSalaries', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('managementSalaries', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('managementSalaries', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.hrAccountingExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.hrAccountingExpenses[year])}
                                                                onChange={(e) => handleInputChange('hrAccountingExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('hrAccountingExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('hrAccountingExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.legalConsultancyExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.legalConsultancyExpenses[year])}
                                                                onChange={(e) => handleInputChange('legalConsultancyExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('legalConsultancyExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('legalConsultancyExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.officeEquipmentExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.officeEquipmentExpenses[year])}
                                                                onChange={(e) => handleInputChange('officeEquipmentExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('officeEquipmentExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('officeEquipmentExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.generalOfficeExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.generalOfficeExpenses[year])}
                                                                onChange={(e) => handleInputChange('generalOfficeExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('generalOfficeExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('generalOfficeExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.rentExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.rentExpenses[year])}
                                                                onChange={(e) => handleInputChange('rentExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('rentExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('rentExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.communicationExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.communicationExpenses[year])}
                                                                onChange={(e) => handleInputChange('communicationExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('communicationExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('communicationExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.trainingExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.trainingExpenses[year])}
                                                                onChange={(e) => handleInputChange('trainingExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('trainingExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('trainingExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.managementTravelExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.managementTravelExpenses[year])}
                                                                onChange={(e) => handleInputChange('managementTravelExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('managementTravelExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('managementTravelExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>

                                                <tr className="input-row">
                                                    <td>{t('financialTable.softwareLicenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.softwareLicenses[year])}
                                                                onChange={(e) => handleInputChange('softwareLicenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('softwareLicenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('softwareLicenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.representationExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.representationExpenses[year])}
                                                                onChange={(e) => handleInputChange('representationExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('representationExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('representationExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.insuranceExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.insuranceExpenses[year])}
                                                                onChange={(e) => handleInputChange('insuranceExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('insuranceExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('insuranceExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.consultingExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.consultingExpenses[year])}
                                                                onChange={(e) => handleInputChange('consultingExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('consultingExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('consultingExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.membershipExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.membershipExpenses[year])}
                                                                onChange={(e) => handleInputChange('membershipExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('membershipExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('membershipExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="total-row inner-total-row">
                                                    <td>{t('financialTable.totalGeneralAdminExpensesDetail')}</td>
                                                    {activeYears.map(year => <td key={year}>{currency}{formatNumber(results.totalGeneralAdminExpenses[year])}</td>)}
                                                </tr>
                                            </tbody>
                                        </table>
                                    </Accordion>
                                </td>
                            </tr>

                            <tr>
                                <td colSpan={activeYears.length + 1} className="accordion-cell">
                                    <Accordion title={t('financialTable.financingTitle')}>
                                        <table className="inner-table">
                                            <tbody>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.creditInterests')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.creditInterests[year])}
                                                                onChange={(e) => handleInputChange('creditInterests', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('creditInterests', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('creditInterests', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.bankCommissions')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.bankCommissions[year])}
                                                                onChange={(e) => handleInputChange('bankCommissions', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('bankCommissions', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('bankCommissions', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.leasingExpenses')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.leasingExpenses[year])}
                                                                onChange={(e) => handleInputChange('leasingExpenses', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('leasingExpenses', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('leasingExpenses', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="input-row">
                                                    <td>{t('financialTable.maturityDifferences')}</td>
                                                    {activeYears.map(year => (
                                                        <td key={year}>
                                                            <input
                                                                type="text"
                                                                value={formatNumber(inputs.maturityDifferences[year])}
                                                                onChange={(e) => handleInputChange('maturityDifferences', year, e.target.value.replace(/\./g, ""))}
                                                                onFocus={(e) => handleOnFocus('maturityDifferences', year, e.target.value)}
                                                                onBlur={(e) => handleOnBlur('maturityDifferences', year, e.target.value)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr className="total-row inner-total-row">
                                                    <td>{t('financialTable.totalFinancingExpensesDetail')}</td>
                                                    {activeYears.map(year => <td key={year}>{currency}{formatNumber(results.totalFinancingExpenses[year])}</td>)}
                                                </tr>
                                            </tbody>
                                        </table>
                                    </Accordion>
                                </td>
                            </tr>
                            <tr className="total-row">
                                <td>{t('financialTable.grossProfitLabel')}</td>
                                {activeYears.map(year => <td key={year}>{currency}{formatNumber(results.grossProfit[year])}</td>)}
                            </tr>
                            <tr className="input-row">
                                <td>{t('financialTable.depreciationExpenses')}</td>
                                {activeYears.map(year => (
                                    <td key={year}>
                                        <input
                                            type="text"
                                            value={formatNumber(inputs.depreciationExpenses[year])}
                                            onChange={(e) => handleInputChange('depreciationExpenses', year, e.target.value.replace(/\./g, ""))}
                                            onFocus={(e) => handleOnFocus('depreciationExpenses', year, e.target.value)}
                                            onBlur={(e) => handleOnBlur('depreciationExpenses', year, e.target.value)}
                                        />
                                    </td>
                                ))}
                            </tr>



                            <tr className="input-row">
                                <td>{t('financialTable.taxAmount')}</td>
                                {activeYears.map(year => (
                                    <td key={year}>
                                        <input
                                            type="text"
                                            value={formatNumber(inputs.taxAmount[year])}
                                            onChange={(e) => handleInputChange('taxAmount', year, e.target.value.replace(/\./g, ""))}
                                            onFocus={(e) => handleOnFocus('taxAmount', year, e.target.value)}
                                            onBlur={(e) => handleOnBlur('taxAmount', year, e.target.value)}
                                        />
                                    </td>
                                ))}
                            </tr>



                            <tr className="total-row">
                                <td>{t('financialTable.ebit')}</td>
                                {activeYears.map(year => <td key={year}>{currency}{formatNumber(results.ebit[year])}</td>)}
                            </tr>
                            <tr className="total-row">
                                <td>{t('financialTable.percentageEbit')}</td>
                                {activeYears.map(year => <td key={year}>%{results.percentageEbit[year].toFixed(2)}</td>)}
                            </tr>
                            <tr className="total-row">
                                <td>{t('financialTable.ebitda')}</td>
                                {activeYears.map(year => <td key={year}>{currency}{formatNumber(results.ebitda[year])}</td>)}
                            </tr>
                            <tr className="total-row">
                                <td>{t('financialTable.percentageEbitda')}</td>
                                {activeYears.map(year => <td key={year}>%{results.percentageEbitda[year].toFixed(2)}</td>)}
                            </tr>
                            {/* 
                            <tr className="calculated-row">
                                <td>İndirgenmiş EBITDA (%40)</td>
                                {activeYears.map(year => <td key={year}>{currency}{formatNumber(results.discountedEbitda[year])}</td>)}
                            </tr>

                             <tr className="total-row">
                                <td>Terminal Değer</td>
                                <td colSpan={activeYears.length} style={{ textAlign: 'right' }}>
                                    {currency}{formatNumber(results.terminalValue * exchangeRate)}
                                </td>
                            </tr>
                            <tr className="total-row">
                                <td>Değerleme Hesabı (Ort. İnd. EBITDA)</td>
                                <td colSpan={activeYears.length} style={{ textAlign: 'right' }}>
                                    {currency}{formatNumber(results.valuationMetric * exchangeRate)}
                                </td>
                            </tr> */}


                        </tbody>
                    </table>
                </div>
                <div className="form-navigation">
                    <div className="nav-buttons-group">
                        <button
                            type="button"
                            className="save-draft-btn"
                            onClick={saveDraft}
                            disabled={isSaving}
                        >
                            {isSaving ? t('financialTable.saving') : t('financialTable.saveForLater')}
                        </button>
                        <button type="button" className="next-button" onClick={handleExportExcel} >
                            <FiDownload /> {t('financialTable.exportExcel')}
                        </button>
                    </div>
                    <div className="nav-buttons-group">
                        <button
                            type="button"
                            className="back-button"
                            onClick={handleBack}
                        >
                            ← {t('financialTable.previous')}
                        </button>
                        <button
                            type="button"
                            onClick={handleNext}
                            className="next-button"
                        >
                            {isDcfOnly
                                ? (i18n.language?.startsWith('en') ? 'Complete' : 'Tamamla')
                                : <>{t('financialTable.next')} →</>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FinancialTable;
